import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { type GoalId, newId, type ProjectId } from '~/types/ids'
import type { Centimetres, Kilograms } from '~/types/units'
import { toCentimetres, toKilograms } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

export type GoalStatus = 'active' | 'achieved' | 'abandoned'

/**
 * Goal and target ranges.
 *
 * Checkpoint 1.1 persists the D-037 minimum — a target date — plus the optional
 * ranges from docs/data-model.md. Projection paths depend on L-001 and are not
 * modelled here.
 */
export interface GoalRecord extends RecordEnvelope {
  readonly id: GoalId
  /** The project this goal belongs to (D-062). See the note on `PhaseRecord`. */
  readonly projectId?: ProjectId
  readonly startDate: CalendarDate
  readonly targetDate: CalendarDate
  readonly status: GoalStatus
  readonly targetWeightMinKg?: Kilograms
  readonly targetWeightMaxKg?: Kilograms
  readonly targetWaistMinCm?: Centimetres
  readonly targetWaistMaxCm?: Centimetres
  readonly desiredVisualOutcome?: string
  /** Maximum acceptable strength decline, as a positive percentage. */
  readonly maxStrengthDeclinePercent?: number
  readonly notes?: string
}

/**
 * Optional target ranges are canonical numbers — kilograms and centimetres —
 * because conversion from a display unit happens at the input boundary before
 * anything reaches this model (D-017, D-041).
 */
export interface GoalInput {
  /** Set by every caller that has a project; carried through unchanged. */
  readonly projectId?: ProjectId
  readonly startDate: string
  readonly targetDate: string
  readonly targetWeightMinKg?: number
  readonly targetWeightMaxKg?: number
  readonly targetWaistMinCm?: number
  readonly targetWaistMaxCm?: number
  readonly notes?: string
}

export function validateGoal(input: GoalInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const start = toCalendarDate(input.startDate)
  const target = toCalendarDate(input.targetDate)

  if (start === undefined) {
    issues.push(issue('startDate', 'Start date must be a real date in YYYY-MM-DD form.'))
  }
  if (target === undefined) {
    issues.push(issue('targetDate', 'Target date must be a real date in YYYY-MM-DD form.'))
  }
  if (start !== undefined && target !== undefined && unwrapDate(target) < unwrapDate(start)) {
    issues.push(issue('targetDate', 'Target date must not be before the start date.'))
  }

  /* Each bound stands on its own: "no heavier than 78 kg" is a usable target
     without a lower bound, so a lone value is accepted. Only the ordering of a
     pair is checked, and neither is given a default: a goal weight or waist is
     personal to the user, and unlike the phase's weekly-change range (D-065)
     there is nothing for the app to suggest. */
  issues.push(
    ...rangeIssues(
      'targetWeight',
      'Target weight',
      input.targetWeightMinKg,
      input.targetWeightMaxKg,
    ),
    ...rangeIssues('targetWaist', 'Target waist', input.targetWaistMinCm, input.targetWaistMaxCm),
  )

  return issues
}

function rangeIssues(
  field: string,
  label: string,
  min: number | undefined,
  max: number | undefined,
): ValidationIssues {
  const issues: ValidationIssue[] = []

  for (const [bound, value] of [
    ['minimum', min],
    ['maximum', max],
  ] as const) {
    if (value !== undefined && !(Number.isFinite(value) && value > 0)) {
      issues.push(issue(field, `${label} ${bound} must be a number greater than zero.`))
    }
  }

  if (min !== undefined && max !== undefined && Number.isFinite(min) && Number.isFinite(max)) {
    if (min > max) {
      issues.push(issue(field, `${label} minimum must not exceed the maximum.`))
    }
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/**
 * Identity carried across an edit.
 *
 * An edit must not mint a new goal or silently revive an abandoned one, so the
 * caller supplies the existing identity and the builder preserves it verbatim —
 * the same reasoning as `existingCreatedAt` on the profile builder. Omit it and
 * a new active goal is created.
 */
export interface ExistingGoalIdentity {
  readonly id: GoalId
  readonly projectId?: ProjectId
  readonly status: GoalStatus
  readonly createdAt: IsoTimestamp
}

/** Build a validated goal record without touching storage. */
export function buildGoalRecord(
  input: GoalInput,
  now: IsoTimestamp,
  existing?: ExistingGoalIdentity,
): Result<GoalRecord, ValidationIssues> {
  const issues = validateGoal(input)
  if (issues.length > 0) return err(issues)

  const startDate = toCalendarDate(input.startDate)
  const targetDate = toCalendarDate(input.targetDate)
  if (startDate === undefined || targetDate === undefined) {
    return err([issue('goal', 'Goal dates failed construction.')])
  }

  /* Validation has already rejected anything non-positive, so these constructors
     only carry the branding across. A bound the user left blank stays absent —
     clearing a target is a real edit, not a failure to supply one. */
  const optional = {
    targetWeightMinKg: optionalKilograms(input.targetWeightMinKg),
    targetWeightMaxKg: optionalKilograms(input.targetWeightMaxKg),
    targetWaistMinCm: optionalCentimetres(input.targetWaistMinCm),
    targetWaistMaxCm: optionalCentimetres(input.targetWaistMaxCm),
  }

  /* As with a phase: an edit keeps the goal in the project it belonged to. */
  const projectId = existing?.projectId ?? input.projectId

  return ok({
    id: existing?.id ?? newId<'GoalId'>(),
    ...(projectId === undefined ? {} : { projectId }),
    startDate,
    targetDate,
    status: existing?.status ?? 'active',
    ...(optional.targetWeightMinKg === undefined
      ? {}
      : { targetWeightMinKg: optional.targetWeightMinKg }),
    ...(optional.targetWeightMaxKg === undefined
      ? {}
      : { targetWeightMaxKg: optional.targetWeightMaxKg }),
    ...(optional.targetWaistMinCm === undefined
      ? {}
      : { targetWaistMinCm: optional.targetWaistMinCm }),
    ...(optional.targetWaistMaxCm === undefined
      ? {}
      : { targetWaistMaxCm: optional.targetWaistMaxCm }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

function optionalKilograms(value: number | undefined): Kilograms | undefined {
  return value === undefined ? undefined : toKilograms(value)
}

function optionalCentimetres(value: number | undefined): Centimetres | undefined {
  return value === undefined ? undefined : toCentimetres(value)
}
